import React, { useEffect, useState } from "react";
import {
  Avatar,
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextField,
  OutlinedInput,
  InputAdornment,
  Link,
  IconButton,
  useTheme,
  InputLabel,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import "../../styles/inputBox.css";
import AiIcon from "../../assets/images/AiIconDark.svg";
import uploadFileIcon from "../../assets/images/uploadFileIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import { multiPartPUTRequest, multiPartRequest } from "../../api/apiServices";
import { createContent, updateContent } from "../../api/apiList";
import Toast from "../../utils/Toast";
import { getAIImages } from "../../utils/AIGenerateImage";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../Loader";
import dayjs, { Dayjs } from "dayjs";
import { generateText } from "../../utils/AIGenerateText";
import { getMediaType } from "../../utils/helper";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import PostingLoader from "../../utils/PostingLoader";
import AIDialog from "../AIDialogBox";
import SchedulePostDialog from "../Common/SchedulePostDialog";
import Modal from "@mui/material/Modal";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import X_text from "../UserInstructions/X_text";
import X_image from "../UserInstructions/X_image";
import X_imageSquare from "../UserInstructions/X_imageSquare";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { getCurrentSubscription } from "../../helper/subscriptionHelper";

// Format Define
const Format = {
  X_TEXT: "X_Text",
  X_IMAGE: "X_Image",
  X_IMAGE_SQUARE: "X_Image_Square",
  X_IMAGE_AD: "X_Image_Ads",
  X_VIDEO_AD: "X_Video_Ads",
  X_CAROUSEL_AD: "X_Carousel_Ads",
};

// Defeine Format validation
const sizeLimits = {
  [Format.X_TEXT]: {
    types: ["text"],
    maxWidth: 1080,
    maxHeight: 1080,
  },
  [Format.X_IMAGE]: {
    types: ["image"],
    maxWidth: 1600,
    maxHeight: 900,
  },
  [Format.X_IMAGE_SQUARE]: {
    types: ["image"],
    maxWidth: 1080,
    maxHeight: 1080,
  },
  [Format.X_IMAGE_AD]: {
    types: ["image"],
    maxWidth: 800,
    maxHeight: 418,
  },
  [Format.X_VIDEO_AD]: {
    types: ["video"],
    maxWidth: 1200,
    maxHeight: 1200,
  },
  [Format.X_CAROUSEL_AD]: {
    types: ["video", "image"],
    maxWidth: 800,
    maxHeight: 450,
  },
};

const TwitterPost: React.FC<{
  selectedFormat: string;
  socialData: any;
  mode: boolean;
}> = ({ selectedFormat, socialData, mode }) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");
  const params = useParams();
  const [format, setFormat] = useState<string>(selectedFormat);
  const [profile, setProfile] = useState<File | string | null>(null);
  const [postText, setPostText] = useState("");
  const [aiImages, setAIImages] = useState<string[]>([]);
  const [prompt, setPrompt] = useState("");
  const [aiDialogOpen, setAIDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("postNow");
  const [scheduledDateTime, setScheduledDateTime] = useState<Dayjs | null>(
    null
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [openPostPreview, setOpenPostPreview] = React.useState(false);
  const companyProfile = useSelector((state: RootState) => state.company);
  const navigate = useNavigate();

  const PostPreviewStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isTabs ? "auto" : 800,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    outline: 0,
    borderRadius: "12px",
  };

  // Validation of Form
  const [errors, setErrors] = useState({
    format: "",
    profile: "",
    postText: "",
  });

  // Define Validation function
  const formIsValid = () => {
    const newErrors = {
      format: !format?.trim() ? "Please Select Format is required" : "",
      profile:
        format === Format.X_TEXT
          ? ""
          : !profile
          ? "Please Upload Format Regarding File."
          : "",
      postText: !postText ? "Post Text Is Required" : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  useEffect(() => {
    setFormat(selectedFormat);
    if (mode && socialData?.content) {
      setProfile(socialData?.content?.photo);
      setPostText(socialData?.content?.postText);
      setScheduledDateTime(dayjs(socialData?.content?.scheduleDateAndTime));
      return;
    }
    setProfile(null);
    setPostText("");
    setErrors((prev) => ({ ...prev, profile: "", postText: "" }));
    setAIImages([]);
  }, [selectedFormat]);

  const handleGridClick = () => document.getElementById("file-upload")?.click();

  // Function to handle opening confirmation dialog
  const handleOpenConfirmation = () => setConfirmationOpen(true);

  // Function to handle closing confirmation dialog
  const handleCloseConfirmation = () => setConfirmationOpen(false);

  // select radiobutton
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedOption(event.target.value);

  // Function to handle scheduling post
  const handleSchedulePost = async () => {
    const { subscription, isActive } = await getCurrentSubscription();
    if (!isActive) {
      navigate("/subscriptions");
      return;
    }
    
    if (subscription?.type?.toLowerCase() === "basic") {
      navigate("/update-subscriptions");
      return;
    }

    if (!scheduledDateTime || !scheduledDateTime.isAfter(dayjs())) {
      Toast("Please select a future date and time.", "error");
      return;
    }
    handleCloseConfirmation();
    let formattedDate;
    if (scheduledDateTime) {
      formattedDate = dayjs(scheduledDateTime).format("YYYY-MM-DD HH:mm:ss");
    }
    const data = {
      name: "Twitter",
      selectFormat: format,
      photo: profile,
      postText,
      scheduleDateAndTime: formattedDate,
      isSchedule: true,
    };
    if (mode) {
      setSubmitLoading(true);
      const response = await multiPartPUTRequest(
        updateContent(params?.id),
        data
      );
      if (response) {
        Toast(response?.data?.statusMessage || "Content Created Successfully");
        navigate("/posting-publishing");
      }
      setSubmitLoading(false);
    } else {
      setSubmitLoading(true);
      const response = await multiPartRequest(createContent, data);
      if (response) {
        Toast(response?.data?.statusMessage || "Content Update Successfully");
        navigate("/posting-publishing");
      }
      setSubmitLoading(false);
    }
  };

  const handleOpenPostPreview = () => setOpenPostPreview(true);
  const handleClosePostPreview = () => setOpenPostPreview(false);

  // Function to handle immediate post
  const handleImmediatePost = async () => {
    handleCloseConfirmation();
    const data = {
      name: "Twitter",
      selectFormat: format,
      photo: profile,
      postText,
    };
    setSubmitLoading(true);
    // multiPartRequest(createContent, data)
    //   .then((response) => {})
    //   .catch((error) => {})
    //   .finally(() => {
    //     setSubmitLoading(false);
    //   });
    // setTimeout(() => {
    //   setSubmitLoading(false);
    //   navigate('/posting-publishing');
    // }, 10000);
    const response = await multiPartRequest(createContent, data);
    if (response) {
      Toast(response?.data?.statusMessage || "Content Created Successfully");
      navigate("/posting-publishing");
    }
    setSubmitLoading(false);
  };

  // Submit Function And Call The API
  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (formIsValid()) {
      handleOpenConfirmation();
    } else {
      // Only show format error toast if format is not selected
      if (!format?.trim()) {
        Toast("Please select a Format", "error");
      }
    }
  };

  // Upload Image Function
  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // call the validation function
      const isValid = await imageAndTypeValidation(file, selectedFormat);
      if (isValid) {
        setProfile(file);
        setErrors((prevErrors) => ({
          ...prevErrors,
          profile: "",
        }));
      } else {
        Toast("Invalid media type or size for the selected format.", "error");
        event.target.value = "";
      }
    }
  };

  // Define The Size and Type Validation
  const imageAndTypeValidation = (file: File, fromat: string) => {
    const formatRules = sizeLimits[fromat];
    if (!formatRules) return false;

    if (formatRules.types) {
      if (!formatRules.types.some((type) => file?.type.startsWith(type))) {
        return false;
      }
    }

    if (file?.type.startsWith("image")) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      return new Promise<boolean>((resolve) => {
        img.onload = () => {
          if (
            img.width > formatRules.maxWidth ||
            img.height > formatRules.maxHeight
          ) {
            resolve(false);
          } else {
            resolve(true);
          }
        };
      });
    }

    if (file?.type.startsWith("video")) {
      const video = document.createElement("video");
      video.preload = "metadata";
      return new Promise<boolean>((resolve) => {
        video.onloadedmetadata = () => {
          if (
            video.videoWidth > formatRules.maxWidth ||
            video.videoHeight > formatRules.maxHeight
          ) {
            resolve(false);
          } else {
            resolve(true);
          }
        };
        video.src = URL.createObjectURL(file);
      });
    }
    return false;
  };

  const handleClearImage = () => setProfile(null);

  // Open Dialog
  const handleGenerateImageClick = () => setAIDialogOpen(true);

  const handlePromptChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setPrompt(event.target.value);
  };

  // Genrating Image With AI
  const handleGenerateImages = async () => {
    try {
      setAIImages([]);
      setAIDialogOpen(false);
      setLoading(true);
      const additionalInfo = `Generate this image with the following input as the use case ${prompt}.`;
      let imagePrompt = "";
      switch (format) {
        case "X_Image":
          imagePrompt = `Create a high-quality X (Twitter) image post (1600x900 px, 16:9 aspect ratio) optimized for visibility and branding. Format: JPG or PNG, max 5MB. Design should be clean, professional, and minimalist, incorporating primaryColor. Include a strong visual CTA (e.g., "Apply Now") if applicable. Avoid excessive text; use strong contrast for readability. Adhere to best practices for X image posts. If relevant, incorporate companyVisionAndMission, industry, services, employerValueProposition, companyMileStones, and/or companyCulture as input for generating this image and use this information from assistant and ${additionalInfo}`;
          break;
        case "X_IMAGE_SQUARE":
          imagePrompt = `Create a high-quality X (Twitter) image post (1080x1080 px, 1:1 aspect ratio) optimized for visibility and branding. Format: JPG or PNG, max 5MB. Design should be clean, professional, and minimalist, incorporating primaryColor. Include a strong visual CTA (e.g., "Apply Now") if applicable. Avoid excessive text; focus on clear visuals with strong contrast for readability. Adhere to best practices for X square image posts. If relevant, incorporate companyVisionAndMission, industry, services, employerValueProposition, companyMileStones, and/or companyCulture as input for generating this image and use this information from assistant and ${additionalInfo}`;
          break;
        default:
          imagePrompt = `${additionalInfo}`;
          break;
      }

      const generatedImages = await getAIImages(
        imagePrompt,
        companyProfile?.assistantId,
        companyProfile?.threadId
      );
      setAIImages(generatedImages);
    } catch (error) {
      console.error("Error generating images:", error);
    } finally {
      setLoading(false);
      setAIDialogOpen(false);
    }
  };

  // Select Of Genration AI Image
  const handleSelectAIImage = (image: string) => setProfile(image);

  // genrate Text With AI
  const generateTextWithAI = async () => {
    setAiLoading(true);
    const additionalInfo = `Include a strong call-to-action (CTA) if relevant (e.g., "Comment below," "Share your thoughts"). Incorporate up to 3 relevant hashtags for visibility and tag individuals or companies (@username) when applicable. Use line breaks for readability but avoid excessive emojis or special characters. Adhere to best practices for this platform. If applicable, integrate name, companyVisionAndMission, industry, services, employerValueProposition, companyMileStones , and/or companyCulture as input for generating this post and use this information from ai assistant which is stored in to the assistance. Write this post with the following input as the use case "${postText}".`;

    let postTextPrompt = "";

    switch (format) {
      case "X_Text":
        postTextPrompt = `Write a concise, engaging text-only post for X (formerly Twitter) within 280 characters, optimized for high engagement. Capture attention immediately to encourage interaction. Use a professional yet conversational tone, as appropriate. ${additionalInfo}`;
        break;
      case "X_Image":
        postTextPrompt = `Write a concise, engaging X (Twitter) post (image size: 1600x900 px, 280 characters max) optimized for high engagement. Hook readers in the first few words. Use a professional yet conversational tone. ${additionalInfo}`;
        break;
      case "X_IMAGE_SQUARE":
        postTextPrompt = `Write a concise, engaging X (Twitter) post (1080x1080 px image, 280 characters max) optimized for high engagement. Hook readers early. Use a clear, professional yet conversational tone. ${additionalInfo}`;
        break;
      default:
        postTextPrompt = `${postText}`;
        break;
    }
    const data = await generateText(
      postTextPrompt,
      companyProfile?.assistantId,
      companyProfile?.threadId
    );
    setPostText(data);
    setAiLoading(false);
  };

  return (
    <div>
      <Grid mt="28px"></Grid>
      <Container
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: isTabs ? "0px 0px 0px 0px" : " 0px 16px 0px 16px",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="884px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={isMobile ? "10px" : ""}
            >
              <Grid item width="fit-content">
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  {(() => {
                    switch (format) {
                      case Format?.X_TEXT:
                        return "X (Twitter) Text only Post";
                      case Format?.X_IMAGE:
                        return "X (Twitter) Image Post | Recommended";
                      case Format?.X_IMAGE_SQUARE:
                        return "X (Twitter) Image Post | Square";
                      default:
                        return "X Post";
                    }
                  })()}
                </Typography>
                <Typography color="#273848" fontWeight={500} fontSize="12.94px">
                  {(() => {
                    switch (format) {
                      case Format?.X_TEXT:
                        return "I used for quick updates, engagement, and thought leadership without images, videos, or links. It is commonly used for company announcements, job openings, polls, and industry insights. This format is also effective for employer branding and recruitment by highlighting company values and work culture in a direct, concise manner. ";
                      case Format?.X_IMAGE:
                        return "is used for visual storytelling, branding, and promotions to enhance engagement and visibility. It is commonly used for employer branding, recruitment marketing, product promotions, and industry insights. This format effectively highlights company culture, job openings, key data, and services.";
                      case Format?.X_IMAGE_SQUARE:
                        return "Is optimized for high engagement and visibility on both mobile and desktop feeds. It is commonly used for employer branding, recruitment marketing, infographics, and product promotions. This format effectively highlights job postings, company culture, industry insights, and key data in a visually engaging way.";
                      default:
                        return "X Post";
                    }
                  })()}
                </Typography>
              </Grid>
            </Grid>
            <Grid my={4}></Grid>

            <Grid item>
              <Typography className="input-label-custom">Post Text</Typography>
              <Grid mb={1}></Grid>

              <OutlinedInput
                placeholder={(() => {
                  switch (format) {
                    case Format?.X_TEXT:
                      return "Write a text for a X post for a quick announcement regarding a new IT project";
                    case Format?.X_IMAGE:
                      return "Write a text for an X (Twitter) post for a new Software Developer vacancy";
                    case Format?.X_IMAGE_SQUARE:
                      return "Write a text for an X (Twitter) post for a new Software Developer vacancy";
                    default:
                      return "Type here...";
                  }
                })()}
                multiline
                minRows={4}
                maxRows={10}
                style={{ display: "inline-block", padding: "25px 11px" }}
                className="text-area-size"
                value={postText}
                onChange={(e) => {
                  const value = e.target.value;
                  setPostText(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      postText: "",
                    }));
                  }
                }}
                endAdornment={
                  <InputAdornment
                    position="end"
                    style={{ justifyContent: "end", height: "auto" }}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      // justifyContent="flex-end"
                      // width="100%"
                      // marginBottom="2%"
                      onClick={generateTextWithAI}
                    >
                      <Grid
                        // className="user-scale"
                        item
                        bgcolor="#ffffff"
                        padding="5px 22px 5px 16px"
                        width="fit-content"
                        borderRadius="6px"
                        border="1px solid #273848"
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          container
                          direction="row"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={aiLoading ? "20px" : "10px"}
                          style={{
                            minWidth: "150px",
                          }}
                        >
                          <img src={AiIcon} alt="instagram-icon" />
                          <Typography
                            fontWeight={500}
                            fontSize="12.55px"
                            color="#273848"
                          >
                            {aiLoading ? <Loader /> : "Generate Text with AI"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </InputAdornment>
                }
              />
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.postText}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            {format !== Format.X_TEXT && (
              <>
                <Grid item>
                  <Typography className="input-label-custom">Image</Typography>
                  <Grid mb={1}></Grid>
                  <Grid
                    item
                    border="1px solid #D5D5D5"
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    display="flex"
                    borderRadius="20px"
                    // padding={isMobile ? "30px" : "18px 230px 48px 230px"}
                    onClick={handleGridClick}
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      width: "100%",
                    }}
                    width="100%"
                  >
                    {profile ? (
                      <>
                        {typeof profile === "string" ? (
                          <>
                            {getMediaType(profile) === "video" ? (
                              <video
                                controls
                                src={profile}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  height: "200px",
                                  width: "150px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <img
                                src={profile}
                                alt="selected-image"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  height: "200px",
                                  width: "150px",
                                  objectFit: "contain",
                                }}
                              />
                            )}
                            <CloseIcon
                              onClick={handleClearImage}
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                cursor: "pointer",
                                color: "#4E4E4E",
                              }}
                            />
                          </>
                        ) : (
                          <>
                            {profile.type.startsWith("image") ? (
                              <img
                                src={URL.createObjectURL(profile)}
                                alt="selected-image"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  height: "200px",
                                  width: "150px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : profile.type.startsWith("video") ? (
                              <video
                                controls
                                src={URL.createObjectURL(profile)}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  height: "200px",
                                  width: "150px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : null}
                            <CloseIcon
                              onClick={handleClearImage}
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                cursor: "pointer",
                                color: "#4E4E4E",
                              }}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <img
                          src={uploadFileIcon}
                          alt="uploadFile-Icon"
                          style={{ marginTop: "2%" }}
                        />
                        <Grid my={1}></Grid>
                        <Typography align="center" color="#4E4E4E">
                          Drag a file or{" "}
                          <Link
                            // href="/signin"
                            color="#4E4E4E"
                            style={{ textDecorationColor: "#4E4E4E" }}
                          >
                            select
                          </Link>
                        </Typography>
                      </>
                    )}
                    <input
                      id="file-upload"
                      type="file"
                      style={{ display: "none" }}
                      accept="image/*, video/*"
                      onChange={handleImageChange}
                    />
                  </Grid>
                  <InputLabel
                    style={{
                      color: "#f44336",
                      paddingTop: "3px",
                      fontSize: "12px",
                      paddingLeft: "10px",
                    }}
                  >
                    {errors.profile}
                  </InputLabel>
                  <>
                    <Grid my={1}></Grid>
                    <Grid
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                      }}
                    >
                      <Grid
                        // className="user-scale"
                        item
                        bgcolor="transparent"
                        padding="9px 22px 9px 16px"
                        width="fit-content"
                        borderRadius="6px"
                        border="1px solid #273848"
                        marginBottom="2%"
                        marginTop={"13px"}
                        marginLeft={"5px"}
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          container
                          direction="row"
                          display="flex"
                          alignItems="center"
                          gap="7px"
                          onClick={handleGenerateImageClick}
                        >
                          <img src={AiIcon} alt="instagram-icon" />
                          <Typography
                            fontWeight={500}
                            fontSize="12.55px"
                            color="#273848"
                          >
                            Generate Image with AI
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        bgcolor="transparent"
                        padding="9px 22px 9px 16px"
                        width="fit-content"
                        borderRadius="6px"
                        border="1px solid #273848"
                        marginBottom="2%"
                        marginTop={"13px"}
                        marginLeft={"5px"}
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          container
                          direction="row"
                          display="flex"
                          alignItems="center"
                          gap="7px"
                          // onClick={handleGenerateImageClick}
                          // onClick={showPreview}
                          onClick={handleOpenPostPreview}
                        >
                          <img src={AiIcon} alt="instagram-icon" />
                          <Typography
                            fontWeight={500}
                            fontSize="12.55px"
                            color="#273848"
                          >
                            Preview
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                  <AIDialog
                    open={aiDialogOpen}
                    title="Generate Image with AI"
                    onClose={() => setAIDialogOpen(false)}
                    onGenerate={handleGenerateImages}
                    value={prompt}
                    onChange={handlePromptChange}
                    cancelButtonName="Cancel"
                    confirmButtonName="Generate"
                  />

                  {loading && (
                    <Grid container justifyContent="center">
                      <CircularProgress style={{ marginTop: "20px" }} />
                    </Grid>
                  )}

                  {aiImages.length > 0 && (
                    <Grid container spacing={2}>
                      {aiImages.map((image, index) => (
                        <Grid
                          item
                          key={index}
                          xs={6}
                          sm={3}
                          onClick={() => handleSelectAIImage(image)}
                        >
                          <img
                            src={image}
                            alt={`AI-generated-${index}`}
                            style={{
                              marginTop: "15px",
                              width: "100%",
                              cursor: "pointer",
                              border: "1px solid #CCCCCC",
                              borderRadius: "7px",
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
                <Grid my={2}></Grid>
                {/* <Grid item width="fit-content">
                  <Typography color="#4E4E4E" fontWeight={600} fontSize="16px">
                    Additional Informations
                  </Typography>
                  <Grid container direction="column" gap={1} mt={2}>
                    <Typography
                      color="#4E4E4E"
                      fontWeight={500}
                      fontSize="16px"
                    >
                      <strong>Image:</strong>
                    </Typography>
                    <Typography
                      color="#4E4E4E"
                      fontWeight={500}
                      fontSize="15px"
                    >
                      - Supports the following file extensions:
                      <strong> .jpg, .png, .gif, .bmp, .tiff</strong>
                    </Typography>

                    <Typography
                      color="#4E4E4E"
                      fontWeight={500}
                      fontSize="16px"
                      mt={2}
                    >
                      <strong>Video:</strong>
                    </Typography>
                    <Typography
                      color="#4E4E4E"
                      fontWeight={500}
                      fontSize="15px"
                    >
                      - Supports the following file extensions:{" "}
                      <strong>.mp4, .mov, .avi, .mkv, .webm</strong>
                    </Typography>
                  </Grid>
                </Grid> */}
              </>
            )}

            {/* User Instructions */}
            {(() => {
              switch (format) {
                case Format?.X_TEXT:
                  return <X_text />;
                case Format?.X_IMAGE:
                  return <X_image />;
                case Format?.X_IMAGE_SQUARE:
                  return <X_imageSquare />;
                default:
                  return "";
              }
            })()}

            <Grid my={4}></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={2}></Grid>
              <Grid item style={{ maxWidth: "fitContent" }}>
                <button
                  type="submit"
                  // className="user-scale"
                  // variant="contained"
                  // fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                    width: "100%",
                    border: "none",
                    color: "white",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  {submitLoading ? <Loader /> : "Submit Post"}
                </button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <SchedulePostDialog
          open={confirmationOpen}
          onClose={handleCloseConfirmation}
          footer={
            <>
              <button
                type="button"
                className="premium-dialog-btn premium-dialog-btn--cancel"
                onClick={handleCloseConfirmation}
              >
                Cancel
              </button>
              {selectedOption === "schedulePost" ? (
                <button
                  type="button"
                  className="premium-dialog-btn premium-dialog-btn--confirm"
                  onClick={handleSchedulePost}
                >
                  {submitLoading ? "Posting..." : "Schedule Post"}
                </button>
              ) : (
                <button
                  type="button"
                  className="premium-dialog-btn premium-dialog-btn--confirm"
                  onClick={handleImmediatePost}
                >
                  {submitLoading ? "Posting..." : "Post Now"}
                </button>
              )}
            </>
          }
        >
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="post-options"
              name="post-options"
              value={selectedOption}
              onChange={handleRadioChange}
            >
              {mode ? (
                <FormControlLabel
                  value="schedulePost"
                  control={<Radio />}
                  label="Schedule Post"
                />
              ) : (
                <>
                  <FormControlLabel
                    value="postNow"
                    control={<Radio />}
                    label="Post Now"
                  />
                  <FormControlLabel
                    value="schedulePost"
                    control={<Radio />}
                    label="Schedule Post"
                  />
                </>
              )}
            </RadioGroup>
          </FormControl>

          {selectedOption === "schedulePost" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoItem label="Select Date and Time:">
                <MobileDateTimePicker
                  value={scheduledDateTime}
                  onChange={(date) => setScheduledDateTime(dayjs(date))}
                />
              </DemoItem>
            </LocalizationProvider>
          )}
        </SchedulePostDialog>
      </Container>
      <PostingLoader
        loading={submitLoading}
        text={
          selectedOption === "schedulePost"
            ? "Wait... we are schedule your content...!"
            : "Wait... we are publishing your content...!"
        }
      />
      <Grid mt="28px"></Grid>

      {/* POST PREVIEW MODAL  */}
      <Modal
        open={openPostPreview}
        onClose={handleClosePostPreview}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={PostPreviewStyle}>
          <>
            <Grid
              container
              alignItems="center"
              justifyContent="flex-start"
              style={{ padding: "20px" }}
            >
              <ArrowBackIcon
                style={{ cursor: "pointer" }}
                onClick={handleClosePostPreview}
              />
              <Typography
                style={{ cursor: "pointer", marginLeft: "10px" }}
                onClick={handleClosePostPreview}
              >
                Back
              </Typography>
            </Grid>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              style={{ minHeight: "80vh" }}
            >
              <div className="previewcard">
                <div className="btn1"></div>
                <div className="btn2"></div>
                <div className="btn3"></div>
                <div className="btn4"></div>
                <div
                  className="topimage"
                  style={{
                    overflow: "hidden",
                    height: "130px",
                    marginTop: "-25px",
                    borderTopLeftRadius: "20px",
                    borderTopRightRadius: "20px",
                  }}
                >
                  <img
                    src="https://images.immediate.co.uk/production/volatile/sites/30/2019/06/kiwi-aedbdbf.jpg?quality=90&resize=556,505"
                    alt="earth static image"
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="footer-left">
                    <IconButton>
                      <FavoriteBorderIcon />
                    </IconButton>
                    <IconButton>
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                    <IconButton>
                      <SendIcon />
                    </IconButton>
                  </div>
                  <div className="footer-right">
                    <IconButton>
                      <BookmarkBorderIcon />
                    </IconButton>
                  </div>
                </div>

                <div className="top-para">
                  <p
                    style={{
                      padding: "10px",
                      paddingTop: "0px",
                      marginTop: "-5px",
                      fontSize: "12px",
                    }}
                  >
                    {" "}
                    <span style={{ fontWeight: "bold" }}>k_0021</span>&nbsp;
                    Crunchy and juicy, apples are sweet and satisfying. They
                    come in a variety of flavors.
                  </p>
                  <Typography
                    style={{
                      padding: "10px",
                      paddingTop: "0px",
                      marginTop: "-17px",
                      fontSize: "10px",
                    }}
                  >
                    2 week ago
                  </Typography>
                </div>
                <div className="header">
                  <Avatar
                    src={
                      companyProfile?.logo ||
                      "https://randomuser.me/api/portraits/men/32.jpg"
                    }
                    className="avatar"
                  />
                  <Typography className="username">
                    {companyProfile?.name || "b_parker024"}
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    <MoreHorizIcon className="more-icon" />
                  </div>
                </div>
                <div className="content" style={{ overflow: "hidden" }}>
                  {profile && (
                    <div
                      style={{
                        display: "flex",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        maxHeight: "630px",
                      }}
                    >
                      {typeof profile !== "string" &&
                      profile.type.startsWith("image") ? (
                        <img
                          src={URL.createObjectURL(profile)}
                          alt="selected-image"
                          className="facebook-image"
                        />
                      ) : typeof profile !== "string" &&
                        profile.type.startsWith("video") ? (
                        <video
                          controls
                          src={URL.createObjectURL(profile)}
                          className="facebook-video"
                        />
                      ) : (
                        typeof profile === "string" && (
                          <img
                            src={profile}
                            alt="selected-image"
                            className="facebook-image"
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
                <div className="footer">
                  <div className="footer-left">
                    <IconButton>
                      <FavoriteBorderIcon />
                    </IconButton>
                    <IconButton>
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                    <IconButton>
                      <SendIcon />
                    </IconButton>
                  </div>
                  <div className="dots"></div>
                  <div className="footer-right">
                    <IconButton>
                      <BookmarkBorderIcon />
                    </IconButton>
                  </div>
                </div>
                <div className="post-info" style={{ borderTop: "none" }}>
                  <Typography variant="body1" style={{ fontSize: "11px" }}>
                    <strong>{companyProfile?.name || "b_parker024"}</strong>{" "}
                    {postText}
                  </Typography>
                </div>
                <div className="time">
                  <Typography
                    style={{
                      padding: "10px",
                      paddingTop: "0px",
                      marginTop: "-18px",
                      fontSize: "10px",
                    }}
                  >
                    Just Now
                  </Typography>
                </div>

                <div className="static-content">
                  <div className="header" style={{ marginTop: "0px" }}>
                    <Avatar
                      src="https://randomuser.me/api/portraits/men/98.jpg"
                      className="avatar"
                    />
                    <Typography className="username">Anirudh Thapa</Typography>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <MoreHorizIcon className="more-icon" />
                    </div>
                  </div>
                  <div
                    className="static-image"
                    style={{
                      overflow: "hidden",
                      height: "40px",
                      marginTop: "10px",
                    }}
                  >
                    <img
                      src="https://pics.craiyon.com/2023-09-16/e448d260ae7b43af8f09413dc89c8f17.webp"
                      alt="earth static image"
                    />
                  </div>
                </div>
                <div className="top">
                  <div className="camera">
                    <div className="int"></div>
                  </div>
                  <div className="speaker"></div>
                </div>
                <div className="bottom-line"></div>
              </div>
            </Grid>
          </>
        </Box>
      </Modal>
    </div>
  );
};

export default TwitterPost;
